/**
 * Plantilla HTML del chrome militar — sidebar + header + footer
 * compartidos por todas las pantallas autenticadas.
 *
 * Capas (z-index ascendente):
 *   0 — `.chrome-bg-wood`    Madera dentro del recuadro interior.
 *   1 — `.chrome-bg-chasis`  Marco de cuero a viewport completo.
 *   2 — `.chrome` (grid)     UI navegable.
 *
 * Las páginas que viven dentro del chrome inyectan su contenido en
 * `<main class="chrome-main">` (zona central de madera).
 */

import type { AuthenticatedPilot } from '@51esc/shared';

/** Identificadores estables de cada sección del producto. */
export type ChromeSection =
  | 'sala'
  | 'ready-room'
  | 'operaciones'
  | 'briefings'
  | 'reportes'
  | 'debriefings'
  | 'logbook'
  | 'greenie-board'
  | 'cvn-quals'
  | 'cvn-top10'
  | 'notams'
  | 'galeria'
  | 'mi-ficha'
  | 'config'
  | 'admin'
  | 'salir';

interface MenuItem {
  section: ChromeSection;
  label: string;
  /** Si es null se renderiza atenuado (coming-soon) o como SALIR (data-action). */
  href: string | null;
  variant: 'bverde' | 'bclaro' | 'bmedio' | 'boscuro';
  /** Solo visible si el rol es admin/superadmin. */
  adminOnly?: boolean;
  /** Si true, no se atenúa: ejecuta data-action="logout". */
  isLogout?: boolean;
}

/** Etiqueta legible que se pinta como "sección actual" bajo el escudo. */
const SECTION_LABELS: Record<ChromeSection, string> = {
  sala: 'SALA DE OPERACIONES',
  'ready-room': 'READY ROOM',
  operaciones: 'OPERACIONES',
  briefings: 'BRIEFINGS',
  reportes: 'REPORTES',
  debriefings: 'DEBRIEFINGS',
  logbook: 'LOGBOOK',
  'greenie-board': 'GREENIE BOARD',
  'cvn-quals': 'QUALS & TRAPS',
  'cvn-top10': 'TOP 10',
  notams: 'NOTAMs',
  galeria: 'GALERÍA',
  'mi-ficha': 'MI FICHA',
  config: 'CONFIGURACIÓN',
  admin: 'ADMIN',
  salir: 'CERRAR SESIÓN',
};

/** Mapeo rol-backend → texto legible que sale en el bloque USUARIO. */
const ROLE_LABELS: Record<string, string> = {
  pilot: 'PILOTO',
  officer: 'OFICIAL',
  admin: 'ADMIN',
  superadmin: 'SUPERADMIN',
};

/**
 * Estructura completa del menú lateral. Cada sub-array es un grupo
 * separado por una regla verde horizontal (`<hr class="chrome-menu-sep">`).
 *
 * Hrefs reales solo en lo que existe hoy:
 *  - SALA → `/`
 *  - REPORTES → `/reportes.html` (galería; el botón "Subir reporte"
 *    dentro chequea rol Oficial+).
 *  - SALIR → handler JS (`data-action="logout"`).
 *
 * El resto van con `href = null` y se atenúan visualmente.
 */
const MENU_GROUPS: MenuItem[][] = [
  [
    { section: 'sala', label: 'SALA', href: '/', variant: 'bverde' },
    {
      section: 'ready-room',
      label: 'READY ROOM',
      href: '/portaaviones.html',
      variant: 'bverde',
    },
  ],
  [
    { section: 'operaciones', label: 'OPERACIONES', href: '/operaciones.html', variant: 'bclaro' },
    { section: 'briefings', label: 'BRIEFINGS', href: '/briefings.html', variant: 'bclaro' },
    { section: 'reportes', label: 'REPORTES', href: '/reportes.html', variant: 'bclaro' },
    { section: 'debriefings', label: 'DEBRIEFINGS', href: '/debriefings.html', variant: 'bclaro' },
  ],
  [
    { section: 'logbook', label: 'LOGBOOK', href: '/logbook.html', variant: 'bmedio' },
    { section: 'notams', label: 'NOTAMs', href: '/notams.html', variant: 'bmedio' },
    { section: 'galeria', label: 'GALERÍA', href: '/galeria.html', variant: 'bmedio' },
  ],
  [
    { section: 'mi-ficha', label: 'MI FICHA', href: null, variant: 'boscuro' },
    // CONFIG desactivado hasta que haya plan funcional — el botón del CRT
    // ya cubre los ajustes visibles (perfil de monitor, brillo, modo
    // día/tarde/noche). Reintroducir aquí cuando exista una página de
    // ajustes propios de la app (no del monitor).
    { section: 'admin', label: 'ADMIN', href: '/admin.html', variant: 'boscuro', adminOnly: true },
  ],
  [{ section: 'salir', label: 'LOGOUT', href: null, variant: 'bverde', isLogout: true }],
];

/**
 * Devuelve el HTML completo del chrome (fondos + grid de UI).
 * Pensado para hacer `document.body.innerHTML = renderChrome(...)`.
 */
export function renderChrome(
  user: AuthenticatedPilot | null,
  activeSection: ChromeSection,
): string {
  return `
    <div class="chrome-bg-wood" aria-hidden="true"></div>
    <img
      class="chrome-bg-chasis"
      src="/chrome/Chasis.png"
      alt=""
      aria-hidden="true"
    />

    <div class="chrome">
      <header class="chrome-header" aria-hidden="true"></header>

      <aside class="chrome-sidebar chrome-sidebar--left" aria-label="Navegación">
        <div class="chrome-brand">
          <img
            class="chrome-brand-shield"
            src="/chrome/51sqn16bits.png"
            alt="Escudo del 51 Escuadrón"
          />
          <span class="chrome-brand-line1">51 ESCUADRÓN</span>
          <span class="chrome-brand-line2">${escapeHtml(SECTION_LABELS[activeSection])}</span>
        </div>

        <nav class="chrome-menu" aria-label="Menú principal">
          ${renderMenu(user, activeSection)}
        </nav>

        ${user ? renderUserBlock(user) : renderAnonBlock()}
      </aside>

      <main class="chrome-main" aria-label="Contenido"></main>

      <aside class="chrome-sidebar chrome-sidebar--right" aria-label="Acciones">
        <section class="chrome-actions">
          <h2 class="chrome-actions-title">ACCIONES</h2>
          <p class="chrome-actions-empty">— sin acciones —</p>
        </section>
      </aside>

      <footer class="chrome-footer">
        <span class="chrome-footer-version">SISTEMA 51SQN v2.3.1</span>
        <span class="chrome-footer-status">
          <span class="chrome-footer-led" aria-hidden="true"></span>
          CONECTADO
        </span>
      </footer>
    </div>
  `;
}

function renderMenu(
  user: AuthenticatedPilot | null,
  activeSection: ChromeSection,
): string {
  const isAdminUser = user?.role === 'admin' || user?.role === 'superadmin';
  const isAnon = user === null;

  const groupsHtml = MENU_GROUPS.map((group) => {
    const items = group
      .filter((item) => !item.adminOnly || isAdminUser)
      // Anónimo: ocultar el botón SALIR (no hay sesión que cerrar).
      .filter((item) => !(isAnon && item.isLogout))
      .map((item) => renderMenuItem(item, activeSection, user))
      .join('');
    if (items === '') return '';
    return `<div class="chrome-menu-section">${items}</div>`;
  })
    .filter((s) => s !== '')
    .join('<hr class="chrome-menu-sep" />');

  return groupsHtml;
}

function renderMenuItem(
  item: MenuItem,
  activeSection: ChromeSection,
  user: AuthenticatedPilot | null,
): string {
  const variantClass = `chrome-menu-item--${item.variant}`;
  const classes = ['chrome-menu-item', variantClass];

  const isActive = item.section === activeSection;
  if (isActive) classes.push('chrome-menu-item--active');

  // MI FICHA: href dinámico al logbook del piloto en sesión. Si no hay
  // sesión, queda como null y se atenúa (mismo trato que coming-soon).
  const effectiveHref =
    item.section === 'mi-ficha' && user
      ? `/logbook/piloto.html?cs=${encodeURIComponent(user.callsign)}`
      : item.href;

  // SALIR es siempre clickable (no se atenúa); el resto sin href se atenúa.
  const isComingSoon = effectiveHref === null && !item.isLogout;
  if (isComingSoon) classes.push('chrome-menu-item--coming-soon');
  if (item.isLogout) classes.push('chrome-menu-item--logout');

  const href = effectiveHref ?? '#';
  const dataAttrs: string[] = [];
  if (item.isLogout) dataAttrs.push('data-action="logout"');
  if (isActive) dataAttrs.push('aria-current="page"');
  if (isComingSoon) dataAttrs.push('aria-disabled="true"');

  return `
    <a class="${classes.join(' ')}" href="${escapeHtml(href)}" ${dataAttrs.join(' ')}>
      <span>${escapeHtml(item.label)}</span>
    </a>
  `;
}

/**
 * Sustituye el bloque USUARIO cuando la página acepta visitantes
 * anónimos. Un único link "INICIAR SESIÓN" que vuelve a la página
 * actual tras autenticarse — útil en `/galeria.html`.
 */
function renderAnonBlock(): string {
  const back = encodeURIComponent(
    typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/',
  );
  return `
    <div class="chrome-user chrome-user--anon">
      <a class="chrome-user-login" href="/login.html?return=${back}">
        INICIAR SESIÓN
      </a>
    </div>
  `;
}

function renderUserBlock(user: AuthenticatedPilot): string {
  const roleLabel = ROLE_LABELS[user.role] ?? user.role.toUpperCase();
  const lastAccess = formatLastAccess(user.lastSeenAt);
  return `
    <div class="chrome-user">
      <div class="chrome-user-row">
        <span class="chrome-user-key">USUARIO</span>
        <span class="chrome-user-val">${escapeHtml(user.callsign)}</span>
      </div>
      <div class="chrome-user-row">
        <span class="chrome-user-key">ROL</span>
        <span class="chrome-user-val">${escapeHtml(roleLabel)}</span>
      </div>
      <div class="chrome-user-row">
        <span class="chrome-user-key">ÚLTIMO ACCESO</span>
        <span class="chrome-user-val">${escapeHtml(lastAccess)}</span>
      </div>
    </div>
  `;
}

/**
 * Formatea un ISO timestamp como `dd/mm/yyyy hh:mm` en hora local.
 * Si no hay valor, devuelve "—" (no inventamos hora).
 */
function formatLastAccess(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
